/** biome-ignore-all lint/suspicious/noExplicitAny: ok */
import { createAsyncThunk, createSlice, PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { serverTimestamp, Timestamp } from 'firebase/firestore';
import _ from 'lodash';
import { Descendant } from 'slate';
import {
  CardData,
  DividerData,
  LatLng,
  ListData,
  TripData,
  TripRole,
} from '../../firebase/firebase.types';
import {
  addEventToDayInTrip,
  clearAllEventsFromItinerary,
  clearEventsFromDay,
  createCardInList,
  createDayAndAddEventInTrip,
  createDividerInTrip,
  createListInDivider,
  createListInTrip,
  createTripDocumentAndLists,
  deleteCardFromList,
  deleteEventInTrip,
  deleteTripDocument,
  getCardsDocs,
  getDividersDocs,
  getListsDocs,
  getTripDocument,
  getTripsForUser,
  moveCard,
  moveEventInTrip,
  moveEventToNewDayInTrip,
  moveListBetweenDividers,
  moveListOutOfDivider,
  moveListToDivider,
  removeDividerInTrip,
  removeListFromDivider,
  removeListInTrip,
  saveDividerListOrder,
  updateCardInTrip,
  updateDayInTrip,
  updateDividerInTrip,
  updateLastOpenedForUser,
  updateListInTrip,
  updateNotesForCard,
  updateTripField,
} from '../../firebase/trips.utils';
import { signOutUser } from '../user/user.slice';
import { AuthUser } from '../user/user.types';
import { getIntersection } from '../utils';
import { changeDefaultDates, changeEventLength, updateDates } from './trip.operations';
import {
  CardID,
  CurrentTrip,
  DateISO,
  DayID,
  DividerID,
  EventID,
  IChangeDefaultDatesThunkResult,
  IChangeEventLengthPayload,
  ISetDefaultDaysPayload,
  ListID,
  Trip,
  TripCard,
  TripCreationStatus,
  TripDivider,
  TripID,
  TripList,
} from './trip.types';
import {
  cardDataToTripCard,
  getDaysMapSnapshot,
  getElementByIdInArray,
  getEventsMapSnapshot,
  getList,
  tripTitleForPlace,
} from './trip.utils';

// ─── State ───────────────────────────────────────────────────────────────────

export const emptyTemplate: Descendant[] = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

export interface TripState {
  readonly trips: Trip[];
  readonly tripCreationStatus: TripCreationStatus;
  readonly currentTrip?: CurrentTrip;
  readonly isLoading: boolean;
  readonly error?: Error | SerializedError;
}

const INITIAL_STATE: TripState = {
  trips: [],
  tripCreationStatus: TripCreationStatus.NOT_CREATED,
  isLoading: false,
};

// ─── Private Helpers ─────────────────────────────────────────────────────────

const getListsSnapshot = async (tripId: TripID): Promise<TripList[]> => {
  const listsDocs = await getListsDocs(tripId);
  const lists: TripList[] = [];

  listsDocs.forEach((listDoc) => {
    const listData = listDoc.data() as ListData;

    // Convert the createdAt field to an ISO string if it exists
    const createdAtISO = listData.createdAt
      ? (listData.createdAt as Timestamp).toDate().toISOString()
      : null;

    // Exclude Timestamp fields from listData to avoid Redux serialization errors
    // Note: lastModifiedAt may exist in older data but is not used
    const { createdAt, lastModifiedAt, ...restListData } = listData as ListData & {
      lastModifiedAt?: Timestamp;
    };

    lists.push({
      id: listDoc.id,
      createdAt: createdAtISO,
      ...restListData, // Spread the rest of the listData (excludes Timestamp fields)
    });
  });

  return lists;
};
const getDividersSnapshot = async (tripId: TripID): Promise<TripDivider[]> => {
  const dividersDocs = await getDividersDocs(tripId);
  const dividers: TripDivider[] = [];

  dividersDocs.forEach((dividerDoc) => {
    const dividerData = dividerDoc.data() as DividerData;
    const createdAtISO = dividerData.createdAt
      ? (dividerData.createdAt as Timestamp).toDate().toISOString()
      : null;
    const { createdAt, ...restDividerData } = dividerData;
    dividers.push({
      id: dividerDoc.id,
      createdAt: createdAtISO,
      ...restDividerData,
    });
  });

  return dividers;
};

const getCardsMapSnapshot = async (tripId: TripID) => {
  const cardsDocs = await getCardsDocs(tripId);
  const cards: { [key: string]: TripCard } = {};

  cardsDocs.forEach((cardDoc) => {
    const cardData = cardDoc.data() as CardData;
    cards[cardDoc.id] = cardDataToTripCard(cardDoc.id, cardData, '');
  });

  return cards;
};

const getCardIdToListIdMap = (lists: TripList[]) => {
  const cardIdToListId: { [key: CardID]: ListID } = {};
  lists.forEach((list) => {
    list.cardOrder?.forEach((cardId) => {
      cardIdToListId[cardId] = list.id;
    });
  });
  return cardIdToListId;
};

// Shared cascade cleanup used by removeList, removeListInDivider, and removeDivider.
// Computes which events to delete and which day eventOrders to patch for a given set of card IDs.
// Scans all events directly rather than using cardIdToEventId, so cards scheduled on multiple
// days (repeated spots) have ALL their events cleaned up, not just the last tracked one.
const buildEventCleanup = (cardIds: CardID[], state: { trip: { currentTrip: Trip } }) => {
  const cardIdSet = new Set(cardIds);
  const eventIdsToDelete = Object.values(state.trip.currentTrip.events)
    .filter((event) => event.id && cardIdSet.has(event.cardId))
    .map((event) => event.id as EventID);
  const daysToUpdate: { [key: DayID]: EventID[] } = {};
  if (state.trip.currentTrip.days) {
    const eventIdsToDeleteSet = new Set(eventIdsToDelete);
    Object.values(state.trip.currentTrip.days).forEach((day) => {
      if (day.id && day.eventOrder) {
        const eventsOnDay = getIntersection(new Set(day.eventOrder), eventIdsToDeleteSet);
        daysToUpdate[day.id] = day.eventOrder.filter((eventId) => !eventsOnDay.has(eventId));
      }
    });
  }
  return { eventIdsToDelete, daysToUpdate };
};

// ─── Trip Thunks ─────────────────────────────────────────────────────────────

export const fetchTrips = createAsyncThunk('trip/fetchAll', async (userId: string) => {
  const tripsSnap = await getTripsForUser(userId);
  const trips: Trip[] = [];

  tripsSnap.forEach((trip) => {
    const tripData = trip.data() as TripData;
    const createdAtISO = tripData.createdAt
      ? (tripData.createdAt as Timestamp).toDate().toISOString()
      : '';

    // Convert Timestamp to ISOstring for lastOpened. Makes it serializable data that can save to redux
    const lastOpened: { [userId: string]: string } = {};
    Object.entries(tripData.lastOpened ?? {}).forEach(([uid, timestamp]: [string, Timestamp]) => {
      lastOpened[uid] = timestamp.toDate().toISOString();
    });

    trips.push({
      id: trip.id,
      ...tripData,
      createdAt: createdAtISO, // Store createdAt as an ISO string
      lastOpened,
      lists: [],
      dividers: [],
      cards: {},
      cardIdToListId: {},
      days: {},
      events: {},
      cardIdToEventId: {},
    });
  });

  return trips;
});

export interface TripCreateProps {
  place: string;
  location: LatLng;
  user: AuthUser;
  startDateISO?: string;
  endDateISO?: string;
}

export const createTrip = createAsyncThunk(
  'trip/create',
  async ({
    place,
    location,
    user,
    startDateISO,
    endDateISO,
  }: TripCreateProps): Promise<CurrentTrip> => {
    const tripDoc = await createTripDocumentAndLists(user.uid, {
      title: tripTitleForPlace(place),
      place,
      location,
      notes: emptyTemplate,
      // Firestore rejects undefined, so only include the dates when both were chosen.
      ...(startDateISO && endDateISO ? { startDateISO, endDateISO } : {}),
      createdAt: serverTimestamp(),
    });
    const lists = await getListsSnapshot(tripDoc.id);

    // Get the trip data from the document
    const tripData = tripDoc.data() as TripData;

    // Convert the createdAt field to an ISO string if it exists
    const createdAtISO = tripData.createdAt
      ? (tripData.createdAt as Timestamp).toDate().toISOString()
      : null;

    return {
      id: tripDoc.id,
      ...tripData,
      createdAt: createdAtISO, // Store createdAt as an ISO string for Redux
      lists,
      dividers: [],
      cards: {},
      cardIdToListId: {},
      days: await getDaysMapSnapshot(tripDoc.id, tripData.startDateISO, tripData.endDateISO),
      events: {},
      cardIdToEventId: {},
      lastOpened: {},
    };
  }
);

/**
 * fetchTrip - Redux Thunk for Loading Trip Data with Access Control
 *
 * This thunk handles the initial loading of trip data and enforces access control
 * at the server/database level before any trip data is returned to the client.
 *
 * ACCESS CONTROL LOGIC:
 * 1. First checks if trip exists in database
 * 2. Then validates user has permission to access the trip via:
 *    - Role-based access: User has owner/editor/viewer role in trip.roles
 *    - Public access: Trip is marked as public (trip.public = true)
 * 3. Throws appropriate error codes for different failure scenarios
 *
 * ERROR CODES:
 * - 'trip-not-found': Trip doesn't exist in database → 404 Not Found
 * - 'permission-denied': Trip exists but user lacks access → 403 Forbidden
 *
 * SUPPORTED ACCESS SCENARIOS:
 * - Authenticated user with role → Full access based on role permissions
 * - Authenticated user without role + public trip → Read-only access
 * - Unauthenticated user + public trip → Read-only access
 * - Any user + private trip without role → Access denied
 */
export const fetchTrip = createAsyncThunk(
  'trip/retrieve',
  async ({ tripID, userID }: { tripID: TripID; userID?: string }): Promise<CurrentTrip> => {
    // STEP 1: Check if trip exists in database
    const tripDoc = await getTripDocument(tripID);
    if (!tripDoc.exists()) {
      const error = new Error(`Trip with ID ${tripID} not found`);
      (error as any).code = 'trip-not-found'; // → 404 Not Found
      throw error;
    }

    const tripData = tripDoc.data() as TripData;

    // STEP 2: Validate access permissions
    // Check if user has explicit role-based access (owner, editor, viewer)
    const hasRoleAccess = userID && tripData.roles && tripData.roles[userID];

    // Check if trip allows public access (no authentication required)
    const isPublicTrip = tripData.public;

    // STEP 3: Enforce access control
    // Deny access if user has neither role access nor public access
    if (!hasRoleAccess && !isPublicTrip) {
      const error = new Error('You do not have permission to access this trip');
      (error as any).code = 'permission-denied'; // → 403 Forbidden
      throw error;
    }

    // ACCESS GRANTED: Load all subcollections in parallel.
    // getDividersSnapshot is wrapped separately — the dividers subcollection requires its own
    // Firestore security rule, and existing trips will return [] until that rule is deployed.
    const [lists, cards, days, events] = await Promise.all([
      getListsSnapshot(tripID),
      getCardsMapSnapshot(tripID),
      getDaysMapSnapshot(tripID, tripData.startDateISO, tripData.endDateISO),
      getEventsMapSnapshot(tripID),
    ]);
    const dividers = await getDividersSnapshot(tripID).catch(() => []);

    const cardIdToListId = getCardIdToListIdMap(lists);
    const cardIdToEventId: { [key: CardID]: EventID } = {};
    Object.values(events).forEach((event) => {
      if (cards[event.cardId]) {
        cards[event.cardId].isInItinerary = true;
        if (event.id) {
          cardIdToEventId[event.cardId] = event.id;
        }
      }
    });

    // Exclude createdAt and lastOpened from the rest of the trip data to prevent non-serializable data from being saved in redux
    const { createdAt, lastOpened, ...restTripData } = tripData;

    // Convert the createdAt field to an ISO string so data can be serializable for Redux
    const createdAtISO = createdAt ? (createdAt as Timestamp).toDate().toISOString() : '';

    const lastOpenedSessionKey = `lastOpened_${tripID}_${userID}`;
    const alreadyUpdatedThisSession = sessionStorage.getItem(lastOpenedSessionKey);
    let lastOpenedISOString = {};

    if (userID && !alreadyUpdatedThisSession) {
      await updateLastOpenedForUser(tripID, userID);
      const nowISO = new Date().toISOString();
      lastOpenedISOString = nowISO;
      sessionStorage.setItem(lastOpenedSessionKey, nowISO);
    } else if (alreadyUpdatedThisSession) {
      lastOpenedISOString = alreadyUpdatedThisSession;
    }

    return {
      id: tripID,
      lists,
      dividers,
      cards,
      cardIdToListId,
      days,
      events,
      createdAt: createdAtISO,
      lastOpened: lastOpenedISOString,
      ...restTripData,
      cardIdToEventId,
    };
  }
);

export interface TripUpdateProps {
  tripId: TripID;
  field: string;
  value: any; // Dynamic field updates require flexible typing
}

export const updateTrip = createAsyncThunk(
  'trip/updateTrip',
  async ({ tripId, field, value }: TripUpdateProps): Promise<TripUpdateProps> => {
    await updateTripField(tripId, field, value);
    return { tripId, field, value };
  }
);

export interface TripDeleteProps {
  tripId: TripID;
}

export const deleteTrip = createAsyncThunk(
  'trip/deleteTrip',
  async ({ tripId }: TripDeleteProps): Promise<TripDeleteProps> => {
    await deleteTripDocument(tripId);
    return { tripId };
  }
);

// ─── List Thunks ─────────────────────────────────────────────────────────────

export interface TripListAddProps {
  tripId: TripID;
  list: ListData;
  listOrder: string[];
}

export interface TripListAddSuccess {
  tripId: TripID;
  list: TripList;
}

export const addList = createAsyncThunk(
  'trip/addList',
  async ({
    tripId,
    list,
    listOrder,
  }: TripListAddProps): Promise<{ tripId: string; list: TripList }> => {
    const listDoc = await createListInTrip(tripId, list, listOrder);
    const listData = listDoc.data() as ListData;

    // Convert the createdAt field to an ISO string if it exists
    const createdAtISO = listData.createdAt
      ? (listData.createdAt as Timestamp).toDate().toISOString()
      : null;

    // Exclude Timestamp fields from listData to avoid Redux serialization errors
    // Note: lastModifiedAt may exist in older data but is not used
    const { createdAt, lastModifiedAt, ...restListData } = listData as ListData & {
      lastModifiedAt?: Timestamp;
    };

    // Return the list in the correct format
    return {
      tripId,
      list: {
        id: listDoc.id,
        createdAt: createdAtISO,
        ...restListData, // Spread the rest of the listData (excludes Timestamp fields)
      } as TripList,
    };
  }
);

export interface TripListRemoveProps {
  tripId: TripID;
  listId: ListID;
  listOrder: ListID[];
  cardIds: CardID[];
}

export interface TripListRemoveSuccess {
  tripId: TripID;
  removedListId: ListID;
  deletedCardIds: CardID[];
  deletedEventIds: EventID[];
}

export const removeList = createAsyncThunk(
  'trip/removeList',
  async (
    { tripId, listId, listOrder, cardIds }: TripListRemoveProps,
    { getState }
  ): Promise<TripListRemoveSuccess> => {
    const state = getState() as { trip: { currentTrip: Trip } };
    const { eventIdsToDelete, daysToUpdate } = buildEventCleanup(cardIds, state);
    await removeListInTrip(tripId, listId, listOrder, cardIds, eventIdsToDelete, daysToUpdate);
    return {
      tripId,
      removedListId: listId,
      deletedCardIds: cardIds,
      deletedEventIds: eventIdsToDelete,
    };
  }
);

export interface TripListOrderUpdateProps {
  tripId: TripID;
  listOrder: ListID[];
  originalListOrder?: ListID[];
}

export const saveListOrder = createAsyncThunk(
  'trip/saveListOrder',
  async ({ tripId, listOrder, originalListOrder }: TripListOrderUpdateProps): Promise<void> => {
    if (!_.isEqual(listOrder, originalListOrder)) {
      await updateTripField(tripId, 'listOrder', listOrder);
    }
  }
);

export interface TripListUpdateProps {
  tripId: TripID;
  listId: ListID;
  field: string;
  value: any; // Dynamic field updates require flexible typing
}

export interface TripListUpdateSuccess {
  listId: ListID;
  field: string;
  value: any; // Dynamic field updates require flexible typing
}

export const updateList = createAsyncThunk(
  'trip/updateList',
  async ({ tripId, listId, field, value }: TripListUpdateProps): Promise<TripListUpdateSuccess> => {
    await updateListInTrip(tripId, listId, field, value);
    return { listId, field, value };
  }
);

// ─── Divider Thunks ──────────────────────────────────────────────────────────

export interface TripDividerAddProps {
  tripId: TripID;
  divider: DividerData;
  listOrder: ListID[];
}

export const addDivider = createAsyncThunk(
  'trip/addDivider',
  async ({
    tripId,
    divider,
    listOrder,
  }: TripDividerAddProps): Promise<{ tripId: TripID; divider: TripDivider }> => {
    const dividerDoc = await createDividerInTrip(tripId, divider, listOrder);
    const dividerData = dividerDoc.data() as DividerData;
    const createdAtISO = dividerData.createdAt
      ? (dividerData.createdAt as Timestamp).toDate().toISOString()
      : null;
    const { createdAt, ...restDividerData } = dividerData;
    return {
      tripId,
      divider: { id: dividerDoc.id, createdAt: createdAtISO, ...restDividerData } as TripDivider,
    };
  }
);

export interface TripDividerListAddProps {
  tripId: TripID;
  dividerId: DividerID;
  list: ListData;
  dividerListOrder: ListID[];
}

export const addListToDivider = createAsyncThunk(
  'trip/addListToDivider',
  async ({
    tripId,
    dividerId,
    list,
    dividerListOrder,
  }: TripDividerListAddProps): Promise<{
    tripId: TripID;
    dividerId: DividerID;
    list: TripList;
  }> => {
    const listDoc = await createListInDivider(tripId, dividerId, list, dividerListOrder);
    const listData = listDoc.data() as ListData;
    const createdAtISO = listData.createdAt
      ? (listData.createdAt as Timestamp).toDate().toISOString()
      : null;
    const { createdAt, lastModifiedAt, ...restListData } = listData as ListData & {
      lastModifiedAt?: Timestamp;
    };
    return {
      tripId,
      dividerId,
      list: { id: listDoc.id, createdAt: createdAtISO, ...restListData } as TripList,
    };
  }
);

export interface TripDividerListRemoveProps {
  tripId: TripID;
  listId: ListID;
  dividerId: DividerID;
  dividerListOrder: ListID[];
  cardIds: CardID[];
}

export const removeListInDivider = createAsyncThunk(
  'trip/removeListInDivider',
  async (
    { tripId, listId, dividerId, dividerListOrder, cardIds }: TripDividerListRemoveProps,
    { getState }
  ): Promise<{
    tripId: TripID;
    listId: ListID;
    dividerId: DividerID;
    deletedCardIds: CardID[];
    deletedEventIds: EventID[];
  }> => {
    const state = getState() as { trip: { currentTrip: Trip } };
    const { eventIdsToDelete, daysToUpdate } = buildEventCleanup(cardIds, state);
    await removeListFromDivider(
      tripId,
      listId,
      dividerId,
      dividerListOrder,
      cardIds,
      eventIdsToDelete,
      daysToUpdate
    );
    return {
      tripId,
      listId,
      dividerId,
      deletedCardIds: cardIds,
      deletedEventIds: eventIdsToDelete,
    };
  }
);

export interface TripDividerRemoveProps {
  tripId: TripID;
  dividerId: DividerID;
  tripListOrder: ListID[];
  nestedListIds: ListID[];
  allCardIds: CardID[];
}

export const removeDivider = createAsyncThunk(
  'trip/removeDivider',
  async (
    { tripId, dividerId, tripListOrder, nestedListIds, allCardIds }: TripDividerRemoveProps,
    { getState }
  ): Promise<{
    tripId: TripID;
    dividerId: DividerID;
    nestedListIds: ListID[];
    deletedCardIds: CardID[];
    deletedEventIds: EventID[];
  }> => {
    const state = getState() as { trip: { currentTrip: Trip } };
    const { eventIdsToDelete, daysToUpdate } = buildEventCleanup(allCardIds, state);
    await removeDividerInTrip(
      tripId,
      dividerId,
      tripListOrder,
      nestedListIds,
      allCardIds,
      eventIdsToDelete,
      daysToUpdate
    );
    return {
      tripId,
      dividerId,
      nestedListIds,
      deletedCardIds: allCardIds,
      deletedEventIds: eventIdsToDelete,
    };
  }
);

export interface TripDividerUpdateProps {
  tripId: TripID;
  dividerId: DividerID;
  field: string;
  value: any;
}

export const updateDivider = createAsyncThunk(
  'trip/updateDivider',
  async ({
    tripId,
    dividerId,
    field,
    value,
  }: TripDividerUpdateProps): Promise<{ dividerId: DividerID; field: string; value: any }> => {
    await updateDividerInTrip(tripId, dividerId, field, value);
    return { dividerId, field, value };
  }
);

export interface TripDividerOrderProps {
  tripId: TripID;
  dividerId: DividerID;
  listOrder: ListID[];
  originalListOrder?: ListID[];
}

export const saveDividerOrder = createAsyncThunk(
  'trip/saveDividerOrder',
  async ({
    tripId,
    dividerId,
    listOrder,
    originalListOrder,
  }: TripDividerOrderProps): Promise<{ dividerId: DividerID }> => {
    if (!_.isEqual(listOrder, originalListOrder)) {
      await saveDividerListOrder(tripId, dividerId, listOrder);
    }
    return { dividerId };
  }
);

export interface SaveListToDividerProps {
  tripId: TripID;
  listId: ListID;
  tripListOrder: ListID[];
  targetDividerId: DividerID;
  targetDividerListOrder: ListID[];
  targetIndex: number;
}

export const saveListToDivider = createAsyncThunk(
  'trip/saveListToDivider',
  async ({
    tripId,
    listId,
    tripListOrder,
    targetDividerId,
    targetDividerListOrder,
    targetIndex,
  }: SaveListToDividerProps): Promise<void> => {
    await moveListToDivider(
      tripId,
      listId,
      tripListOrder,
      targetDividerId,
      targetDividerListOrder,
      targetIndex
    );
  }
);

export interface SaveListOutOfDividerProps {
  tripId: TripID;
  listId: ListID;
  dividerId: DividerID;
  dividerListOrder: ListID[];
  tripListOrder: ListID[];
  targetIndex: number;
}

export const saveListOutOfDivider = createAsyncThunk(
  'trip/saveListOutOfDivider',
  async ({
    tripId,
    listId,
    dividerId,
    dividerListOrder,
    tripListOrder,
    targetIndex,
  }: SaveListOutOfDividerProps): Promise<void> => {
    await moveListOutOfDivider(
      tripId,
      listId,
      dividerId,
      dividerListOrder,
      tripListOrder,
      targetIndex
    );
  }
);

export interface SaveListBetweenDividersProps {
  tripId: TripID;
  listId: ListID;
  fromDividerId: DividerID;
  fromDividerListOrder: ListID[];
  toDividerId: DividerID;
  toDividerListOrder: ListID[];
  targetIndex: number;
}

export const saveListBetweenDividers = createAsyncThunk(
  'trip/saveListBetweenDividers',
  async ({
    tripId,
    listId,
    fromDividerId,
    fromDividerListOrder,
    toDividerId,
    toDividerListOrder,
    targetIndex,
  }: SaveListBetweenDividersProps): Promise<void> => {
    await moveListBetweenDividers(
      tripId,
      listId,
      fromDividerId,
      fromDividerListOrder,
      toDividerId,
      toDividerListOrder,
      targetIndex
    );
  }
);

// ─── Card Thunks ─────────────────────────────────────────────────────────────

export interface TripListCardAddProps {
  tripId: TripID;
  listId: ListID;
  card: CardData;
  cardOrder: string[];
}

export interface TripListCardAddSuccess {
  listId: ListID;
  card: TripCard;
}
export const addCardToList = createAsyncThunk<
  TripListCardAddSuccess,
  TripListCardAddProps,
  { rejectValue: string }
>('trip/addCardToList', async ({ tripId, listId, card, cardOrder }, { rejectWithValue }) => {
  try {
    // Note: deepCleanObject is called inside createCardInList (trips.utils.ts)
    // to sanitize Places API data before Firestore write
    const cardDoc = await createCardInList(tripId, listId, card, cardOrder);
    const cardData = cardDoc.data() as CardData; // Cast cardData to CardData

    return {
      listId,
      card: cardDataToTripCard(cardDoc.id, cardData, null),
    };
  } catch (error) {
    // biome-ignore lint/suspicious/noConsole: error logging for debugging failed card additions
    console.error('addCardToList error:', error);
    return rejectWithValue(error instanceof Error ? error.message : 'Failed to add card');
  }
});

export interface TripListCardRemoveProps {
  tripId: TripID;
  listId: ListID;
  cardId: CardID;
  cardOrder: CardID[];
}

export interface TripListCardRemoveSuccess {
  listId: ListID;
  removedCardId: CardID;
}

export const removeCardFromList = createAsyncThunk(
  'trip/removeCardFromList',
  async (
    { tripId, listId, cardId, cardOrder }: TripListCardRemoveProps,
    { getState }
  ): Promise<TripListCardRemoveSuccess> => {
    const state = getState() as { trip: { currentTrip: Trip } };
    // Find ALL events for this card — a card can appear on multiple days (repeated spots)
    const allEventIdsForCard = Object.values(state.trip.currentTrip.events)
      .filter((event) => event.cardId === cardId && event.id)
      .map((event) => event.id as EventID);
    const eventIdSet = new Set(allEventIdsForCard);
    const daysToUpdate: { dayId: DayID; eventOrder: EventID[] }[] = [];
    if (state.trip.currentTrip.days) {
      Object.values(state.trip.currentTrip.days).forEach((day) => {
        if (day.id && day.eventOrder && day.eventOrder.some((eventId) => eventIdSet.has(eventId))) {
          daysToUpdate.push({
            dayId: day.id,
            eventOrder: day.eventOrder.filter((eventId) => !eventIdSet.has(eventId)),
          });
        }
      });
    }
    await deleteCardFromList(tripId, listId, cardId, cardOrder, allEventIdsForCard, daysToUpdate);
    return { listId, removedCardId: cardId };
  }
);

export interface TripListCardMoveProps {
  tripId: TripID;
  cardId: CardID;
  fromListId: ListID;
  toListId: ListID;
  fromListCardOrder: CardID[];
  toListCardOrder: CardID[];
}

export interface TripListCardMoveSuccess {
  movedCardId: CardID;
  fromListId: ListID;
  toListId: ListID;
}

export const moveCardToList = createAsyncThunk(
  'trip/moveCardToList',
  async ({
    tripId,
    cardId,
    fromListId,
    toListId,
    fromListCardOrder,
    toListCardOrder,
  }: TripListCardMoveProps): Promise<TripListCardMoveSuccess> => {
    await moveCard(tripId, cardId, fromListId, toListId, fromListCardOrder, toListCardOrder);
    return { movedCardId: cardId, fromListId, toListId };
  }
);

export interface TripListCardOrderUpdateProps {
  tripId: TripID;
  listId: ListID;
  cardOrder: CardID[];
  originalCardOrder?: CardID[];
}

export interface TripListCardOrderUpdateSuccess {
  listId: ListID;
}

export const saveCardOrderForActiveList = createAsyncThunk(
  'trip/saveCardOrderForActiveList',
  async ({
    tripId,
    listId,
    cardOrder,
    originalCardOrder,
  }: TripListCardOrderUpdateProps): Promise<TripListCardOrderUpdateSuccess> => {
    if (!_.isEqual(cardOrder, originalCardOrder)) {
      await updateListInTrip(tripId, listId, 'cardOrder', cardOrder);
    }
    return { listId };
  }
);

export interface TripCardUpdateProps {
  tripId: TripID;
  cardId: CardID;
  field: string;
  value: any; // Dynamic field updates require flexible typing
}

export interface TripCardUpdateSuccess {
  cardId: CardID;
  field: string;
  value: any; // Dynamic field updates require flexible typing
}

export const updateCard = createAsyncThunk(
  'trip/updateCard',
  async ({ tripId, cardId, field, value }: TripCardUpdateProps): Promise<TripCardUpdateSuccess> => {
    await updateCardInTrip(tripId, cardId, field, value);
    return { cardId, field, value };
  }
);

export interface TripCardNotesUpdateProps {
  tripId: TripID;
  cardId: CardID;
  notes: Descendant[];
}

export interface TripCardNotesUpdateSuccess {
  cardId: CardID;
  notes: Descendant[];
}

export const updateCardNotes = createAsyncThunk(
  'trip/updateCardNotes',
  async ({
    tripId,
    cardId,
    notes,
  }: TripCardNotesUpdateProps): Promise<TripCardNotesUpdateSuccess> => {
    await updateNotesForCard(tripId, cardId, notes);
    return { cardId, notes };
  }
);

// ─── Event Thunks ────────────────────────────────────────────────────────────

interface TripEventAddToDateProps {
  tripId: TripID;
  dayId?: DayID;
  dateISO: DateISO;
  cardId: CardID;
  eventOrder?: string[];
}

interface TripEventAddToDateSuccess {
  tripId: TripID;
  dayId: DayID;
  dateISO: DateISO;
  cardId: CardID;
  eventId: EventID;
  eventOrder: EventID[];
}

export const addEventToDate = createAsyncThunk(
  'trip/addEventToDate',
  async ({
    tripId,
    dayId,
    dateISO,
    cardId,
    eventOrder,
  }: TripEventAddToDateProps): Promise<TripEventAddToDateSuccess> => {
    if (dayId) {
      const { eventId, eventOrder: newEventOrder } = await addEventToDayInTrip(
        tripId,
        dayId,
        eventOrder ?? [],
        {
          cardId,
          startDateISO: dateISO,
        }
      );
      return {
        tripId,
        dayId,
        dateISO,
        cardId,
        eventId,
        eventOrder: newEventOrder,
      };
    }

    const { dayId: newDayId, eventId } = await createDayAndAddEventInTrip(
      tripId,
      { dateISO },
      { cardId, startDateISO: dateISO }
    );
    return {
      tripId,
      dayId: newDayId,
      dateISO,
      cardId,
      eventId,
      eventOrder: [eventId],
    };
  }
);

interface TripEventMoveToDateProps {
  tripId: TripID;
  fromDayId: DayID;
  toDayId?: DayID;
  fromDateISO: DateISO;
  toDateISO: DateISO;
  eventId: EventID;
  fromDayEventOrder: EventID[];
  toDayEventOrder?: EventID[];
}

interface TripEventMoveToDateSuccess {
  tripId: TripID;
  fromDayId: DayID;
  toDayId: DayID;
  fromDateISO: DateISO;
  toDateISO: DateISO;
  eventId: EventID;
  fromDayEventOrder: EventID[];
  toDayEventOrder: EventID[];
}

export const moveEventToDate = createAsyncThunk(
  'trip/moveEventToDate',
  async ({
    tripId,
    fromDayId,
    toDayId,
    fromDateISO,
    toDateISO,
    eventId,
    fromDayEventOrder,
    toDayEventOrder,
  }: TripEventMoveToDateProps): Promise<TripEventMoveToDateSuccess> => {
    const newFromDayEventOrder = fromDayEventOrder.filter((id) => eventId !== id);
    const newToDayEventOrder = (toDayEventOrder ?? []).concat([eventId]);
    if (toDayId) {
      await moveEventInTrip(
        tripId,
        fromDayId,
        toDayId,
        toDateISO,
        eventId,
        newFromDayEventOrder,
        newToDayEventOrder
      );
      return {
        tripId,
        fromDayId,
        toDayId,
        fromDateISO,
        toDateISO,
        eventId,
        fromDayEventOrder: newFromDayEventOrder,
        toDayEventOrder: newToDayEventOrder,
      };
    }

    const { toDayId: newDayId } = await moveEventToNewDayInTrip(
      tripId,
      fromDayId,
      toDateISO,
      eventId,
      newFromDayEventOrder,
      newToDayEventOrder
    );
    return {
      tripId,
      fromDayId,
      toDayId: newDayId,
      fromDateISO,
      toDateISO,
      eventId,
      fromDayEventOrder: newFromDayEventOrder,
      toDayEventOrder: newToDayEventOrder,
    };
  }
);

interface TripEventDeleteFromDateProps {
  tripId: TripID;
  eventId: EventID;
  cardId: CardID;
  dayId: DayID;
  dateISO: DateISO;
  eventOrder: string[];
}

interface TripEventDeleteFromDateSuccess {
  tripId: TripID;
  eventId: EventID;
  cardId: CardID;
  dateISO: DateISO;
}

/** Removes all events from a single itinerary day. */
export const clearDay = createAsyncThunk(
  'trip/clearDay',
  async (
    {
      tripId,
      dayId,
      dateISO,
      eventIds,
    }: { tripId: TripID; dayId: DayID; dateISO: DateISO; eventIds: EventID[] },
    { rejectWithValue }
  ) => {
    try {
      await clearEventsFromDay(tripId, dayId, eventIds);
      return { tripId, dateISO, eventIds };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/** Removes all events from every day in the itinerary. Reads current state to build the payload. */
export const clearItinerary = createAsyncThunk(
  'trip/clearItinerary',
  async ({ tripId }: { tripId: TripID }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { trip: { currentTrip: Trip } };
      const { days, events } = state.trip.currentTrip;
      const eventIds = Object.keys(events) as EventID[];
      await clearAllEventsFromItinerary(tripId, days, eventIds);
      return { tripId };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteEventFromDate = createAsyncThunk(
  'trip/deleteEventFromDate',
  async ({
    tripId,
    eventId,
    cardId,
    dayId,
    dateISO,
    eventOrder,
  }: TripEventDeleteFromDateProps): Promise<TripEventDeleteFromDateSuccess> => {
    const newEventOrder = eventOrder.filter((id) => eventId !== id);
    await deleteEventInTrip(tripId, dayId, newEventOrder, eventId);
    return { tripId, eventId, cardId, dateISO };
  }
);

export interface TripDayEventOrderUpdateProps {
  tripId: TripID;
  dayId: DayID;
  dateISO: DateISO;
  eventOrder: EventID[];
  dragIndex: number;
  hoverIndex: number;
}

export interface TripDayEventOrderUpdateSuccess {
  tripId: TripID;
  dateISO: DayID;
}

export const updateEventOrderForDay = createAsyncThunk(
  'trip/updateEventOrderForDay',
  async ({
    tripId,
    dayId,
    dateISO,
    eventOrder,
    dragIndex,
    hoverIndex,
  }: TripDayEventOrderUpdateProps): Promise<TripDayEventOrderUpdateSuccess> => {
    const newEventOrder = [...eventOrder];
    const dragItem = newEventOrder.splice(dragIndex, 1)[0];
    newEventOrder.splice(hoverIndex, 0, dragItem);
    await updateDayInTrip(tripId, dayId, 'eventOrder', newEventOrder);
    return { tripId, dateISO };
  }
);

export interface TripDayEventOrderSaveProps {
  tripId: TripID;
  dayId: DayID;
  dateISO: DateISO;
  eventOrder: EventID[];
}

// Persists a pre-built event order (used after local-only hover reorders during DnD)
export const saveEventOrderForDay = createAsyncThunk(
  'trip/saveEventOrderForDay',
  async ({
    tripId,
    dayId,
    dateISO,
    eventOrder,
  }: TripDayEventOrderSaveProps): Promise<TripDayEventOrderUpdateSuccess> => {
    await updateDayInTrip(tripId, dayId, 'eventOrder', eventOrder);
    return { tripId, dateISO };
  }
);

// ─── Slice ───────────────────────────────────────────────────────────────────

export interface TripRolesUpdateProps {
  tripId: TripID;
  rolesToUpdate: {
    userId: string;
    role: TripRole;
  }[];
  usersToDelete: string[];
}

export interface TripTempUpdateOrderProps {
  dragIndex: number;
  hoverIndex: number;
}

export interface TripUpdateCardPhotoReferencesProps {
  tripId: TripID;
  cardId: CardID;
  photoReferences: string[];
}

export interface TripUpdateMapCenterProps {
  lat: number;
  lng: number;
}

export interface TripUpdateLastOpenedProps {
  userId: string;
  lastOpened: Timestamp;
}

const tripSlice = createSlice({
  name: 'trip',
  initialState: INITIAL_STATE,
  reducers: {
    resetTripCreationStatus(state) {
      state.tripCreationStatus = TripCreationStatus.NOT_CREATED;
    },
    clearCurrentTrip(state) {
      state.currentTrip = undefined;
    },
    updateLastOpened: (state, action: PayloadAction<TripUpdateLastOpenedProps>) => {
      if (state.currentTrip && state.currentTrip.lastOpened) {
        const { userId, lastOpened } = action.payload;

        // Convert Firestore Timestamp to ISO string
        const lastOpenedISO: string = lastOpened.toDate().toISOString();

        state.currentTrip.lastOpened[userId] = lastOpenedISO;
      }
    },
    updateTripRoles(state, action: PayloadAction<TripRolesUpdateProps>) {
      const { tripId, rolesToUpdate, usersToDelete } = action.payload;
      if (state.currentTrip && state.currentTrip.id === tripId) {
        rolesToUpdate.forEach((userRole) => {
          if (state.currentTrip) {
            state.currentTrip.roles[userRole.userId] = userRole.role;
          }
        });
        usersToDelete.forEach((userId: string) => {
          if (state.currentTrip) {
            delete state.currentTrip.roles[userId];
          }
        });
      }
    },
    updateTripUserEmails(state, action: PayloadAction<string[]>) {
      if (state.currentTrip) {
        state.currentTrip.userEmails = action.payload;
      }
    },
    updateActiveListId(state, action: PayloadAction<string>) {
      if (state.currentTrip) {
        state.currentTrip.activeListId = action.payload;
      }
    },
    tempUpdateListOrder(state, action: PayloadAction<TripTempUpdateOrderProps>) {
      if (state.currentTrip) {
        if (!state.currentTrip.originalListOrder) {
          state.currentTrip.originalListOrder = [...state.currentTrip.listOrder];
        }
        const { dragIndex, hoverIndex } = action.payload;
        const dragItem = state.currentTrip.listOrder.splice(dragIndex, 1)[0];
        state.currentTrip.listOrder.splice(hoverIndex, 0, dragItem);
      }
    },
    tempUpdateDividerListOrder(
      state,
      action: PayloadAction<{ dividerId: DividerID; dragIndex: number; hoverIndex: number }>
    ) {
      if (state.currentTrip) {
        const divider = state.currentTrip.dividers.find((d) => d.id === action.payload.dividerId);
        if (divider) {
          if (!divider.originalListOrder) {
            divider.originalListOrder = [...divider.listOrder];
          }
          const { dragIndex, hoverIndex } = action.payload;
          const dragItem = divider.listOrder.splice(dragIndex, 1)[0];
          divider.listOrder.splice(hoverIndex, 0, dragItem);
        }
      }
    },
    tempUpdateCardOrder(state, action: PayloadAction<TripTempUpdateOrderProps>) {
      if (state.currentTrip && state.currentTrip.activeListId) {
        const list = getList(state.currentTrip.lists, state.currentTrip.activeListId);
        if (list && list.cardOrder) {
          if (!list.originalCardOrder) {
            list.originalCardOrder = [...list.cardOrder];
          }
          const { dragIndex, hoverIndex } = action.payload;
          const dragItem = list.cardOrder.splice(dragIndex, 1)[0];
          list.cardOrder.splice(hoverIndex, 0, dragItem);
        }
      }
    },
    updateCardPhotoReferences(state, action: PayloadAction<TripUpdateCardPhotoReferencesProps>) {
      const { tripId, cardId, photoReferences } = action.payload;
      if (state.currentTrip && state.currentTrip.id === tripId) {
        if (state.currentTrip.cards[cardId]) {
          state.currentTrip.cards[cardId].photoReferences = photoReferences;
        }
      }
    },
    updateMapCenter(state, action: PayloadAction<TripUpdateMapCenterProps>) {
      if (state.currentTrip) {
        state.currentTrip.mapCenter = action.payload;
      }
    },
    updateMapZoom(state, action: PayloadAction<number>) {
      if (state.currentTrip) {
        state.currentTrip.mapZoom = action.payload;
      }
    },
    setDefaultDays(state, { payload }: PayloadAction<ISetDefaultDaysPayload>) {
      if (state.currentTrip) {
        state.currentTrip.days = payload.days;
      }
    },
    clearDeletedEvents(state) {
      if (state.currentTrip) {
        state.currentTrip.deletedEvents = undefined;
      }
    },
    tempUpdateEventOrder(
      state,
      action: PayloadAction<{ dateISO: DateISO; dragIndex: number; hoverIndex: number }>
    ) {
      if (!state.currentTrip) {
        return;
      }
      const day = state.currentTrip.days[action.payload.dateISO];
      if (!day?.eventOrder) {
        return;
      }
      const { dragIndex, hoverIndex } = action.payload;
      const newOrder = [...day.eventOrder];
      const [dragged] = newOrder.splice(dragIndex, 1);
      newOrder.splice(hoverIndex, 0, dragged);
      day.eventOrder = newOrder;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrips.pending, (state) => {
        state.isLoading = true;
        // Clear previous trips to prevent showing stale data
        state.trips = [];
      })
      .addCase(fetchTrips.fulfilled, (state, action) => {
        state.isLoading = false;
        state.trips = action.payload;
      })
      .addCase(createTrip.pending, (state) => {
        state.tripCreationStatus = TripCreationStatus.CREATING;
      })
      .addCase(createTrip.fulfilled, (state, action) => {
        state.tripCreationStatus = TripCreationStatus.CREATED;
        state.currentTrip = {
          activeListId: action.payload.lists[0].id,
          ...action.payload,
        };
        state.error = undefined;
      })
      .addCase(createTrip.rejected, (state, action) => {
        state.tripCreationStatus = TripCreationStatus.NOT_CREATED;
        state.error = action.error;
      })
      .addCase(fetchTrip.pending, (state) => {
        state.isLoading = true;
        // Clear previous trip data to prevent flashing
        state.currentTrip = undefined;
        state.error = undefined;
      })
      .addCase(fetchTrip.fulfilled, (state, action) => {
        const { listOrder } = action.payload;
        let activeListId = listOrder && listOrder.length > 0 ? listOrder[0] : undefined;
        if (
          state.currentTrip &&
          state.currentTrip.activeListId &&
          listOrder &&
          listOrder.find((listId) => state.currentTrip && listId === state.currentTrip.activeListId)
        ) {
          activeListId = state.currentTrip.activeListId;
        }
        state.isLoading = false;
        state.currentTrip = {
          activeListId,
          lastFetchedAt: new Date().toISOString(),
          ...action.payload,
        };
        state.error = undefined;
      })
      .addCase(fetchTrip.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error;
        state.currentTrip = undefined;
      })
      .addCase(updateTrip.fulfilled, (state, { payload: { tripId, field, value } }) => {
        const trip = getElementByIdInArray(state.trips, tripId) as Trip;
        if (trip && field in trip) {
          trip[field] = value;
        }
        if (state.currentTrip && state.currentTrip.id === tripId && field in state.currentTrip) {
          state.currentTrip[field] = value;
        }
      })
      .addCase(deleteTrip.fulfilled, (state, { payload: { tripId } }) => {
        state.trips = state.trips.filter((trip) => trip.id !== tripId);
      })
      .addCase(addList.fulfilled, (state, { payload: { tripId, list } }) => {
        if (state.currentTrip && state.currentTrip.id === tripId) {
          state.currentTrip.lists.unshift(list);
          state.currentTrip.listOrder.unshift(list.id);
          state.currentTrip.activeListId = list.id;
        }
      })
      .addCase(addList.rejected, (state, action) => {
        // Use development-only logging for errors
        if (process.env.NODE_ENV === 'development') {
          // biome-ignore lint/suspicious/noConsole: should remove these
          console.error('Failed to add list:', action.error);
        }
        // The generic error handler will set state.error, so we don't need to do anything else
        // This case exists to prevent the app from breaking when addList fails
      })
      .addCase(
        removeList.fulfilled,
        (state, { payload: { tripId, removedListId, deletedEventIds } }) => {
          if (state.currentTrip && state.currentTrip.id === tripId) {
            const removedList = state.currentTrip.lists.find((list) => list.id === removedListId);
            removedList?.cardOrder?.forEach((cardId) => {
              if (state.currentTrip) {
                delete state.currentTrip.cards[cardId];
                delete state.currentTrip.cardIdToListId[cardId];
                delete state.currentTrip.cardIdToEventId[cardId];
              }
            });
            state.currentTrip.lists = state.currentTrip.lists.filter(
              (list) => list.id !== removedListId
            );
            state.currentTrip.listOrder = state.currentTrip.listOrder.filter(
              (listId) => listId !== removedListId
            );
            if (state.currentTrip.activeListId === removedListId) {
              if (state.currentTrip.listOrder.length) {
                [state.currentTrip.activeListId] = state.currentTrip.listOrder;
              } else {
                state.currentTrip.activeListId = undefined;
              }
            }
            deletedEventIds.forEach((eventId) => {
              if (state.currentTrip) {
                delete state.currentTrip.events[eventId];
              }
            });
            const eventIdsSet = new Set(deletedEventIds);
            Object.values(state.currentTrip.days).forEach((day) => {
              day.eventOrder = day.eventOrder?.filter((eventId) => !eventIdsSet.has(eventId));
            });
          }
        }
      )
      .addCase(saveListOrder.fulfilled, (state) => {
        if (state.currentTrip) {
          state.currentTrip.originalListOrder = undefined;
        }
      })
      .addCase(updateList.fulfilled, (state, { payload: { listId, field, value } }) => {
        // Update list properties (name, emoji) in real-time for cross-tab synchronization
        if (state.currentTrip) {
          const existingList = getList(state.currentTrip.lists, listId);
          if (existingList) {
            existingList[field] = value;
          }
        }
      })
      .addCase(addCardToList.fulfilled, (state, { payload: { listId, card } }) => {
        if (state.currentTrip) {
          const existingList = getList(state.currentTrip.lists, listId);
          if (existingList) {
            state.currentTrip.cards[card.id] = card;
            if (existingList.cardOrder) {
              existingList.cardOrder.unshift(card.id);
            } else {
              existingList.cardOrder = [card.id];
            }
          }
          state.currentTrip.cardIdToListId[card.id] = listId;
        }
      })
      .addCase(removeCardFromList.fulfilled, (state, { payload: { listId, removedCardId } }) => {
        if (state.currentTrip) {
          const existingList = getList(state.currentTrip.lists, listId);
          if (existingList) {
            delete state.currentTrip.cards[removedCardId];
            existingList.cardOrder = existingList.cardOrder
              ? existingList.cardOrder.filter((cardId: string) => cardId !== removedCardId)
              : undefined;
          }
          // Delete ALL events for this card from state (card may have been on multiple days)
          const eventIdsToRemove = new Set(
            Object.values(state.currentTrip.events)
              .filter((event) => event.cardId === removedCardId && event.id)
              .map((event) => event.id as EventID)
          );
          eventIdsToRemove.forEach((eventId) => {
            delete state.currentTrip!.events[eventId];
          });
          Object.values(state.currentTrip.days).forEach((day) => {
            if (day.eventOrder && day.eventOrder.some((id) => eventIdsToRemove.has(id))) {
              day.eventOrder = day.eventOrder.filter((id) => !eventIdsToRemove.has(id));
            }
          });
          delete state.currentTrip.cardIdToEventId[removedCardId];
          delete state.currentTrip.cardIdToListId[removedCardId];
        }
      })
      .addCase(
        moveCardToList.fulfilled,
        (state, { payload: { movedCardId, fromListId, toListId } }) => {
          if (state.currentTrip) {
            const fromList = getList(state.currentTrip.lists, fromListId);
            const toList = getList(state.currentTrip.lists, toListId);
            if (fromList?.cardOrder && toList) {
              fromList.cardOrder = fromList.cardOrder.filter(
                (cardId: string) => cardId !== movedCardId
              );
              if (toList.cardOrder) {
                toList.cardOrder.unshift(movedCardId);
              } else {
                toList.cardOrder = [movedCardId];
              }
            }
            state.currentTrip.cardIdToListId[movedCardId] = toListId;
          }
        }
      )
      .addCase(saveCardOrderForActiveList.fulfilled, (state, { payload: { listId } }) => {
        if (state.currentTrip) {
          const list = getList(state.currentTrip.lists, listId);
          if (list) {
            list.originalCardOrder = undefined;
          }
        }
      })
      .addCase(updateCard.fulfilled, (state, { payload: { cardId, field, value } }) => {
        if (state.currentTrip) {
          const existingCard = state.currentTrip.cards[cardId];
          if (existingCard) {
            existingCard[field] = value;
          }
        }
      })
      .addCase(updateCardNotes.fulfilled, (state, { payload: { cardId, notes } }) => {
        if (state.currentTrip) {
          const existingCard = state.currentTrip.cards[cardId];
          if (existingCard) {
            existingCard.notes = notes;
          }
        }
      })
      .addCase(
        updateDates.fulfilled,
        (state, { payload: { tripId, startDateISO, endDateISO, days, events, deletedEvents } }) => {
          if (state.currentTrip && state.currentTrip.id === tripId) {
            state.currentTrip.startDateISO = startDateISO;
            state.currentTrip.endDateISO = endDateISO;
            state.currentTrip.days = days;
            state.currentTrip.events = events;
            Object.keys(state.currentTrip.cards).forEach((cardId) => {
              if (state.currentTrip) {
                state.currentTrip.cards[cardId].isInItinerary = false;
                delete state.currentTrip.cardIdToEventId[cardId];
              }
            });
            Object.values(events).forEach((event) => {
              if (state.currentTrip?.cards[event.cardId]) {
                state.currentTrip.cards[event.cardId].isInItinerary = true;
              }
            });
            state.currentTrip.isChangedDefaultDates = false;
            if (deletedEvents.length) {
              state.currentTrip.deletedEvents = deletedEvents;
            }
          }
        }
      )
      .addCase(
        addEventToDate.pending,
        (
          state,
          {
            meta: {
              arg: { tripId, dayId, dateISO, cardId, eventOrder },
            },
          }
        ) => {
          if (state.currentTrip && state.currentTrip.id === tripId) {
            const tripDay = state.currentTrip.days[dateISO];
            if (tripDay) {
              const newEventOrder = eventOrder ? eventOrder.concat([cardId]) : [cardId];
              tripDay.id = dayId;
              tripDay.eventOrder = newEventOrder;
            } else {
              state.currentTrip.days[dateISO] = {
                id: dayId,
                dateISO,
                eventOrder,
              };
            }

            state.currentTrip.events[cardId] = {
              id: cardId,
              cardId,
              startDateISO: dateISO,
              isConfirmed: false,
            };

            if (state.currentTrip.cards[cardId]) {
              state.currentTrip.cards[cardId].isInItinerary = true;
            }
          }
        }
      )
      .addCase(
        addEventToDate.fulfilled,
        (state, { payload: { tripId, dayId, dateISO, cardId, eventId } }) => {
          if (state.currentTrip && state.currentTrip.id === tripId) {
            const tripDay = state.currentTrip.days[dateISO];
            if (tripDay.eventOrder) {
              const index = tripDay.eventOrder.findIndex((value) => value === cardId);
              if (index !== -1) {
                tripDay.eventOrder[index] = eventId;
              }
            }

            state.currentTrip.events[eventId] = {
              id: eventId,
              cardId,
              startDateISO: dateISO,
              isConfirmed: true,
            };
            state.currentTrip.cardIdToEventId[cardId] = eventId;
            state.currentTrip.days[dateISO] = { ...tripDay, id: dayId };

            delete state.currentTrip.events[cardId];
          }
        }
      )
      .addCase(
        addEventToDate.rejected,
        (
          state,
          {
            meta: {
              arg: { tripId, dateISO, cardId, eventOrder },
            },
          }
        ) => {
          if (state.currentTrip && state.currentTrip.id === tripId) {
            const tripDay = state.currentTrip.days[dateISO];
            tripDay.eventOrder = eventOrder;
            delete state.currentTrip.events[cardId];
            if (state.currentTrip.cards[cardId]) {
              state.currentTrip.cards[cardId].isInItinerary = false;
            }
          }
        }
      )
      .addCase(
        moveEventToDate.pending,
        (
          state,
          {
            meta: {
              arg: {
                tripId,
                fromDayId,
                toDayId,
                fromDateISO,
                toDateISO,
                eventId,
                fromDayEventOrder,
                toDayEventOrder,
              },
            },
          }
        ) => {
          if (state.currentTrip?.id === tripId) {
            const newFromDayEventOrder = fromDayEventOrder.filter((id) => eventId !== id);
            const newToDayEventOrder = (toDayEventOrder ?? []).concat([eventId]);
            const fromDay = state.currentTrip.days[fromDateISO];
            const toDay = state.currentTrip.days[toDateISO];
            if (fromDay) {
              fromDay.eventOrder = newFromDayEventOrder;
            } else {
              state.currentTrip.days[fromDateISO] = {
                id: fromDayId,
                dateISO: fromDateISO,
                eventOrder: newFromDayEventOrder,
              };
            }
            if (toDay) {
              toDay.eventOrder = newToDayEventOrder;
            } else {
              state.currentTrip.days[toDateISO] = {
                id: toDayId,
                dateISO: toDateISO,
                eventOrder: newToDayEventOrder,
              };
            }

            state.currentTrip.events[eventId].startDateISO = toDateISO;
            state.currentTrip.events[eventId].isConfirmed = false;
          }
        }
      )
      .addCase(
        moveEventToDate.fulfilled,
        (state, { payload: { tripId, eventId, toDateISO, toDayId } }) => {
          if (state.currentTrip?.id === tripId) {
            state.currentTrip.events[eventId].isConfirmed = true;
            state.currentTrip.days[toDateISO] = {
              ...state.currentTrip.days[toDateISO],
              id: toDayId,
            };
          }
        }
      )
      .addCase(
        moveEventToDate.rejected,
        (
          state,
          {
            meta: {
              arg: {
                tripId,
                fromDayId,
                toDayId,
                fromDateISO,
                toDateISO,
                eventId,
                fromDayEventOrder,
                toDayEventOrder,
              },
            },
          }
        ) => {
          if (state.currentTrip?.id === tripId) {
            const fromDay = state.currentTrip.days[fromDateISO];
            const toDay = state.currentTrip.days[toDateISO];
            if (fromDay) {
              fromDay.eventOrder = fromDayEventOrder;
            } else {
              state.currentTrip.days[fromDateISO] = {
                id: fromDayId,
                dateISO: fromDateISO,
                eventOrder: fromDayEventOrder,
              };
            }
            if (toDay) {
              toDay.eventOrder = toDayEventOrder;
            } else {
              state.currentTrip.days[toDateISO] = {
                id: toDayId,
                dateISO: toDateISO,
                eventOrder: toDayEventOrder,
              };
            }

            state.currentTrip.events[eventId].startDateISO = fromDateISO;
            state.currentTrip.events[eventId].isConfirmed = true;
          }
        }
      )
      .addCase(
        deleteEventFromDate.pending,
        (
          state,
          {
            meta: {
              arg: { tripId, eventId, dateISO, eventOrder },
            },
          }
        ) => {
          if (state.currentTrip?.id === tripId) {
            const newEventOrder = eventOrder.filter((id) => eventId !== id);
            const day = state.currentTrip.days[dateISO];
            if (day) {
              day.eventOrder = newEventOrder;
            }
            const event = state.currentTrip.events[eventId];
            if (event) {
              // Only mark as not in itinerary if no other events reference this card
              const hasOtherEvents = Object.values(state.currentTrip.events).some(
                (e) => e.id !== eventId && e.cardId === event.cardId
              );
              if (!hasOtherEvents) {
                state.currentTrip.cards[event.cardId].isInItinerary = false;
              }
            }
          }
        }
      )
      .addCase(deleteEventFromDate.fulfilled, (state, { payload: { tripId, eventId, cardId } }) => {
        if (state.currentTrip && state.currentTrip.id === tripId) {
          delete state.currentTrip.events[eventId];
          // Update cardIdToEventId: point to a remaining event, or remove if none left
          const remainingEvent = Object.values(state.currentTrip.events).find(
            (e) => e.cardId === cardId
          );
          if (remainingEvent?.id) {
            state.currentTrip.cardIdToEventId[cardId] = remainingEvent.id;
          } else {
            delete state.currentTrip.cardIdToEventId[cardId];
          }
        }
      })
      .addCase(
        deleteEventFromDate.rejected,
        (
          state,
          {
            meta: {
              arg: { tripId, eventId, dateISO, eventOrder },
            },
          }
        ) => {
          if (state.currentTrip?.id === tripId) {
            const day = state.currentTrip.days[dateISO];
            if (day) {
              day.eventOrder = eventOrder;
            }
            const event = state.currentTrip.events[eventId];
            if (event) {
              state.currentTrip.cards[event.cardId].isInItinerary = true;
            }
          }
        }
      )
      .addCase(clearDay.fulfilled, (state, { payload: { tripId, dateISO, eventIds } }) => {
        if (state.currentTrip && state.currentTrip.id === tripId) {
          // Reset the day's event order
          const day = state.currentTrip.days[dateISO];
          if (day) {
            day.eventOrder = [];
          }

          // Collect affected cardIds before deleting events from state
          const affectedCardIds = new Set(
            eventIds
              .map((eventId) => state.currentTrip!.events[eventId]?.cardId)
              .filter(Boolean) as CardID[]
          );

          // Remove the cleared events
          for (const eventId of eventIds) {
            delete state.currentTrip.events[eventId];
          }

          // Update cardIdToEventId — keep if the card still has events elsewhere, remove if not
          for (const cardId of Array.from(affectedCardIds)) {
            const remaining = Object.values(state.currentTrip.events).find(
              (e) => e.cardId === cardId
            );
            if (remaining?.id) {
              state.currentTrip.cardIdToEventId[cardId] = remaining.id;
            } else {
              delete state.currentTrip.cardIdToEventId[cardId];
            }
          }
        }
      })
      .addCase(clearItinerary.fulfilled, (state, { payload: { tripId } }) => {
        if (state.currentTrip && state.currentTrip.id === tripId) {
          // Reset all days and wipe the events + lookup map
          for (const day of Object.values(state.currentTrip.days)) {
            day.eventOrder = [];
          }
          state.currentTrip.events = {};
          state.currentTrip.cardIdToEventId = {};
        }
      })
      .addCase(addDivider.fulfilled, (state, { payload: { tripId, divider } }) => {
        if (state.currentTrip && state.currentTrip.id === tripId) {
          state.currentTrip.dividers.unshift(divider);
          state.currentTrip.listOrder.unshift(divider.id);
        }
      })
      .addCase(addDivider.rejected, (_state, action) => {
        if (process.env.NODE_ENV === 'development') {
          // biome-ignore lint/suspicious/noConsole: debugging divider creation failures
          console.error('Failed to add divider:', action.error);
        }
      })
      .addCase(addListToDivider.fulfilled, (state, { payload: { tripId, dividerId, list } }) => {
        if (state.currentTrip && state.currentTrip.id === tripId) {
          state.currentTrip.lists.unshift(list);
          const divider = state.currentTrip.dividers.find((d) => d.id === dividerId);
          if (divider) {
            divider.listOrder.unshift(list.id);
          }
          state.currentTrip.activeListId = list.id;
        }
      })
      .addCase(
        removeListInDivider.fulfilled,
        (state, { payload: { tripId, listId, dividerId, deletedCardIds, deletedEventIds } }) => {
          if (state.currentTrip && state.currentTrip.id === tripId) {
            deletedCardIds.forEach((cardId) => {
              if (state.currentTrip) {
                delete state.currentTrip.cards[cardId];
                delete state.currentTrip.cardIdToListId[cardId];
                delete state.currentTrip.cardIdToEventId[cardId];
              }
            });
            state.currentTrip.lists = state.currentTrip.lists.filter((l) => l.id !== listId);
            const divider = state.currentTrip.dividers.find((d) => d.id === dividerId);
            if (divider) {
              divider.listOrder = divider.listOrder.filter((id) => id !== listId);
              if (state.currentTrip.activeListId === listId) {
                state.currentTrip.activeListId = divider.listOrder.length
                  ? divider.listOrder[0]
                  : undefined;
              }
            }
            const eventIdsSet = new Set(deletedEventIds);
            deletedEventIds.forEach((eventId) => {
              if (state.currentTrip) {
                delete state.currentTrip.events[eventId];
              }
            });
            Object.values(state.currentTrip.days).forEach((day) => {
              day.eventOrder = day.eventOrder?.filter((id) => !eventIdsSet.has(id));
            });
          }
        }
      )
      .addCase(
        removeDivider.fulfilled,
        (
          state,
          { payload: { tripId, dividerId, nestedListIds, deletedCardIds, deletedEventIds } }
        ) => {
          if (state.currentTrip && state.currentTrip.id === tripId) {
            state.currentTrip.dividers = state.currentTrip.dividers.filter(
              (d) => d.id !== dividerId
            );
            state.currentTrip.listOrder = state.currentTrip.listOrder.filter(
              (id) => id !== dividerId
            );
            const nestedSet = new Set(nestedListIds);
            state.currentTrip.lists = state.currentTrip.lists.filter((l) => !nestedSet.has(l.id));
            deletedCardIds.forEach((cardId) => {
              if (state.currentTrip) {
                delete state.currentTrip.cards[cardId];
                delete state.currentTrip.cardIdToListId[cardId];
                delete state.currentTrip.cardIdToEventId[cardId];
              }
            });
            if (state.currentTrip.activeListId && nestedSet.has(state.currentTrip.activeListId)) {
              state.currentTrip.activeListId = undefined;
            }
            const eventIdsSet = new Set(deletedEventIds);
            deletedEventIds.forEach((eventId) => {
              if (state.currentTrip) {
                delete state.currentTrip.events[eventId];
              }
            });
            Object.values(state.currentTrip.days).forEach((day) => {
              day.eventOrder = day.eventOrder?.filter((id) => !eventIdsSet.has(id));
            });
          }
        }
      )
      .addCase(updateDivider.fulfilled, (state, { payload: { dividerId, field, value } }) => {
        if (state.currentTrip) {
          const divider = state.currentTrip.dividers.find((d) => d.id === dividerId);
          if (divider) {
            divider[field] = value;
          }
        }
      })
      .addCase(saveDividerOrder.fulfilled, (state, { payload: { dividerId } }) => {
        if (state.currentTrip) {
          const divider = state.currentTrip.dividers.find((d) => d.id === dividerId);
          if (divider) {
            divider.originalListOrder = undefined;
          }
        }
      })
      // Moves a top-level list into a divider: removes it from the main list order and adds it to the divider's list order.
      .addCase(saveListToDivider.fulfilled, (state, action) => {
        if (!state.currentTrip) {
          return;
        }
        const { listId, targetDividerId, targetIndex } = action.meta.arg;
        state.currentTrip.listOrder = state.currentTrip.listOrder.filter((id) => id !== listId);
        state.currentTrip.originalListOrder = undefined;
        const divider = state.currentTrip.dividers.find((d) => d.id === targetDividerId);
        if (divider && !divider.listOrder.includes(listId)) {
          divider.listOrder.splice(targetIndex, 0, listId);
        }
      })
      // Moves a list out of a divider to the top level: removes it from the divider and inserts it into the main list order.
      .addCase(saveListOutOfDivider.fulfilled, (state, action) => {
        if (!state.currentTrip) {
          return;
        }
        const { listId, dividerId, targetIndex } = action.meta.arg;
        const divider = state.currentTrip.dividers.find((d) => d.id === dividerId);
        if (divider) {
          divider.listOrder = divider.listOrder.filter((id) => id !== listId);
          divider.originalListOrder = undefined;
        }
        if (!state.currentTrip.listOrder.includes(listId)) {
          state.currentTrip.listOrder.splice(targetIndex, 0, listId);
        }
      })
      // Moves a list from one divider to another: removes it from the source divider and adds it to the target divider.
      .addCase(saveListBetweenDividers.fulfilled, (state, action) => {
        if (!state.currentTrip) {
          return;
        }
        const { listId, fromDividerId, toDividerId, targetIndex } = action.meta.arg;
        const fromDivider = state.currentTrip.dividers.find((d) => d.id === fromDividerId);
        if (fromDivider) {
          fromDivider.listOrder = fromDivider.listOrder.filter((id) => id !== listId);
          fromDivider.originalListOrder = undefined;
        }
        const toDivider = state.currentTrip.dividers.find((d) => d.id === toDividerId);
        if (toDivider && !toDivider.listOrder.includes(listId)) {
          toDivider.listOrder.splice(targetIndex, 0, listId);
        }
      })
      .addCase(signOutUser.fulfilled, () => INITIAL_STATE)
      .addCase(
        changeEventLength.fulfilled,
        (state: TripState, { payload }: PayloadAction<IChangeEventLengthPayload>) => {
          if (state.currentTrip && state.currentTrip.id === payload.tripId) {
            if (payload.eventLength) {
              state.currentTrip.events[payload.eventId].eventLength = payload.eventLength;
            } else {
              delete state.currentTrip.events[payload.eventId].eventLength;
            }
          }
        }
      )
      .addCase(
        changeDefaultDates.fulfilled,
        (state: TripState, { payload }: PayloadAction<IChangeDefaultDatesThunkResult>) => {
          if (state.currentTrip) {
            state.currentTrip.isChangedDefaultDates = true;
            state.currentTrip.startDateISO = payload.startDateISO;
            state.currentTrip.endDateISO = payload.endDateISO;
            state.currentTrip.days = payload.days;
            state.currentTrip.events = payload.events;
          }
        }
      )
      .addCase(
        updateEventOrderForDay.pending,
        (
          state,
          {
            meta: {
              arg: { tripId, dateISO, dragIndex, hoverIndex },
            },
          }
        ) => {
          if (state.currentTrip && state.currentTrip.id === tripId) {
            const day = state.currentTrip.days[dateISO];
            if (day && day.eventOrder) {
              const dragItem = day.eventOrder.splice(dragIndex, 1)[0];
              day.eventOrder.splice(hoverIndex, 0, dragItem);
            }
          }
        }
      )
      .addCase(
        updateEventOrderForDay.rejected,
        (
          state,
          {
            meta: {
              arg: { tripId, dateISO, eventOrder },
            },
          }
        ) => {
          if (state.currentTrip && state.currentTrip.id === tripId) {
            const day = state.currentTrip.days[dateISO];
            if (day) {
              day.eventOrder = eventOrder;
            }
          }
        }
      );
  },
});

export const {
  resetTripCreationStatus,
  clearCurrentTrip,
  updateTripRoles,
  updateActiveListId,
  updateTripUserEmails,
  tempUpdateListOrder,
  tempUpdateDividerListOrder,
  tempUpdateCardOrder,
  tempUpdateEventOrder,
  updateCardPhotoReferences,
  updateMapCenter,
  updateMapZoom,
  setDefaultDays,
  clearDeletedEvents,
} = tripSlice.actions;

export default tripSlice;
